import { ReportPhase, ReportStep } from "./reportSteps";

// The five states the client can be in. 'started' and 'processing' both come off the wire;
// 'detached' is synthesised locally when the client ceiling trips.
export type ReportStatus = "started" | "processing" | "ready" | "failed" | "detached";

export interface DeriveProgressInput {
  steps: ReportStep[];
  // '' until the first 202 answers. Never a terminal value on the wire.
  stage: string;
  status: ReportStatus;
  // -1 at the start of a poll generation. A fresh click or a retry resets it.
  previousIndex: number;
  // The server's failure message, forwarded raw. `unknown` for the same reason
  // SummaryStatusResponse types it that way: the summary job sends a string, the shared /meet
  // rate limiter sends an object. Only read when status is 'failed'.
  error?: unknown;
}

export interface DeriveProgressResult {
  activeIndex: number;
  phase: ReportPhase;
  // True only for a stage the server sent that this build does not know. The caller logs it once
  // per poll generation; it is never shown to the user.
  unknownStage: boolean;
}

// Maps {stage, status} to a step index and a phase. Pure: no refs, no clock, no DOM.
//
// An unrecognised stage must HOLD the display, never collapse to 'transcribing' - collapsing
// would make the first new stage we ship render a confident lie.
//
// R1 status 'ready'    -> the terminal row, phase 'done'.
// R2 status 'failed'   -> the step whose failureMarkers match the server's message, else hold;
//                         phase 'failed'.
// R3 status 'detached' -> hold, phase 'detached'.
// R4 otherwise match `stage` against the keys. No match -> hold and flag.
// R5 the index is monotonic non-decreasing within a poll generation. That is what R4's max()
//    buys: an out-of-order poll response, or an unknown stage, can never drag the display
//    backwards past a step the user has already watched go green. R1 is the one exemption: it
//    jumps straight to the terminal row, which is not a step back because phase 'done' paints
//    every row green anyway.

// The server's failure message, flattened to something matchable. Mirrors coerceSummaryMessage in
// MeetingHistory.tsx rather than importing it: this module is pure and has no React dependency.
const failureText = (error: unknown): string => {
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "";
};

export const deriveProgress = ({
  steps,
  stage,
  status,
  previousIndex,
  error,
}: DeriveProgressInput): DeriveProgressResult => {
  // Never -1. A failure that lands before any stage does still has to blame a row, and row 0 is
  // the only honest candidate.
  const held = Math.max(previousIndex, 0);

  if (status === "ready") {
    // A different guard from `held`, despite the identical shape: this one only stops an empty
    // `steps` array producing -1. It says nothing about previousIndex.
    return { activeIndex: Math.max(steps.length - 1, 0), phase: "done", unknownStage: false };
  }

  if (status === "failed") {
    // `held` alone is only right when the step that broke is the step the client last watched.
    // A step that fails faster than one poll interval is never observed - a revoked OpenAI key
    // fails the report stage in ~11 s against a 10 s poll - so without this the failure lands on
    // the previous row, wearing that row's failureFallback. Browser testing caught exactly
    // that: a stage-2 401 painted on 'Turning audio into text', telling the user the recording
    // had no audio when the transcript was fine.
    //
    // Math.max preserves R5. Attribution may carry the blame FORWARD to a step the user had not
    // seen start; it can never drag it back past one they already watched go green.
    const message = failureText(error);
    const claimed = message
      ? steps.findIndex((step) => step.failureMarkers?.some((marker) => message.includes(marker)))
      : -1;
    return {
      activeIndex: claimed === -1 ? held : Math.max(held, claimed),
      phase: "failed",
      unknownStage: false,
    };
  }

  if (status === "detached") {
    return { activeIndex: held, phase: "detached", unknownStage: false };
  }

  // An empty stage is "not known yet", not "not recognised" - it is what the client holds
  // between the click and the first 202. Flagging it would fire a console warning on every
  // single job start and drown the one case worth reading.
  if (!stage) {
    return { activeIndex: held, phase: "running", unknownStage: false };
  }

  const index = steps.findIndex((step) => step.key === stage);
  if (index === -1) {
    return { activeIndex: held, phase: "running", unknownStage: true };
  }

  return { activeIndex: Math.max(previousIndex, index), phase: "running", unknownStage: false };
};
